import React, { useState, useEffect, useCallback } from 'react';
import { RestoModProvider, useRestoMod } from './context/RestoModContext';
import { WorkshopHeader } from './components/layout/WorkshopHeader';
import { NavigationTabs, ActiveTab } from './components/layout/NavigationTabs';
import { StatusBar } from './components/layout/StatusBar';
import { VisualizerView } from './components/visualizer/VisualizerView';
import { TeardownView } from './components/teardown/TeardownView';
import { BOMView } from './components/bom/BOMView';
import { DossierView } from './components/dossier/DossierView';

/* ============================================================================
   RESTOMOD STUDIO — INTEGRATED WORKSHOP APPLICATION
   Authentic non-glassmorphic restoration suite for classic car builders.
   ============================================================================ */

const VALID_TABS: ActiveTab[] = ['visualizer', 'teardown', 'bom', 'dossier'];

const parseHashTab = (): ActiveTab | null => {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace('#', '').toLowerCase();
  return VALID_TABS.includes(hash as ActiveTab) ? (hash as ActiveTab) : null;
};

const RestoModAppInner: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    return parseHashTab() || 'visualizer';
  });
  const { state } = useRestoMod();

  // Synchronize hash on browser back/forward or manual hash change
  useEffect(() => {
    const handleHashChange = () => {
      const tabFromHash = parseHashTab();
      if (tabFromHash && tabFromHash !== activeTab) {
        setActiveTab(tabFromHash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeTab]);

  // Handle user tab selection with hash synchronization
  const handleSelectTab = useCallback((tab: ActiveTab) => {
    setActiveTab(tab);
    if (typeof window !== 'undefined' && window.location.hash !== `#${tab}`) {
      window.history.replaceState(null, '', `#${tab}`);
    }
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-workshop-grid text-shop-light">
      {/* 1. Header with Vehicle Specs & Global Actions */}
      <WorkshopHeader />

      {/* 2. Navigation Tabs (min 44px touch targets) */}
      <NavigationTabs
        activeTab={activeTab}
        onSelectTab={handleSelectTab}
        teardownCount={state.subAssemblies.length}
        bomCount={state.bomItems.length}
      />

      {/* 3. Main Content Viewport */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6" role="region" aria-label="Workshop Module Viewport">
        {activeTab === 'visualizer' && <VisualizerView />}
        {activeTab === 'teardown' && <TeardownView />}
        {activeTab === 'bom' && <BOMView />}
        {activeTab === 'dossier' && <DossierView />}
      </main>

      {/* 4. Status Bar with Hardware & Budget Variance */}
      <StatusBar />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <RestoModProvider>
      <RestoModAppInner />
    </RestoModProvider>
  );
};

export default App;
